import React, { useEffect } from 'react';
import { Link, useMatch } from 'react-router-dom';
import * as Bixby from '$/core';
import { USER_EMAIL_CONFIRMATION_SUCCESS_MESSAGE } from '$/core/constants';
import { AppState } from '$/store';
import * as UserEmailConfirmationActions from '$/store/user-email-confirmation/actions';
import { connect } from 'react-redux';
import { ReactComponent as Logo } from '$/assets/BixLogo.svg';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import InputBlock from '$/components/global/inputBlock';
import Info from '$/components/global/info/Info';
import Error from '$/components/global/error/Error';
import './index.scss';

const userEmailConfirmationAction = UserEmailConfirmationActions.userEmailConfirmationRequest;
const verifyUserEmailConfirmationTokenAction = UserEmailConfirmationActions.verifyUserEmailConfirmationTokenRequest;

export interface SetPasswordValues {
  password: string;
  confirmPassword: string;
}

interface IUserEmailConfirmationProps {
  dispatchUserEmailConfirmationRequest: (
    UserEmailConfirmationRequest: Bixby.Models.UserEmailConfirmationRequest
  ) => Promise<void>;
  dispatchVerifyUserEmailConfirmationRequest: (
    VerifyUserEmailConfirmationRequest: Bixby.Models.VerifyPasswordResetTokenRequest
  ) => Promise<void>;
  isLoading: boolean;
  error?: string;
  success: boolean;
  tokenInvalid: boolean;
}

const UserEmailConfirmation: React.FC<IUserEmailConfirmationProps> = props => {
  const {
    isLoading,
    error,
    success,
    tokenInvalid,
    dispatchUserEmailConfirmationRequest,
    dispatchVerifyUserEmailConfirmationRequest,
  } = props;

  const match = useMatch('/emailConfirmation/:userId?/:code?');
  const initialPasswordValues: SetPasswordValues = {
    password: '',
    confirmPassword: '',
  };
  useEffect(() => {
    const verifyToken = async () => {
      await dispatchVerifyUserEmailConfirmationRequest({
        userId: match?.params.userId ?? '',
        code: match?.params.code ?? '',
      });
    };
    verifyToken();
  }, [dispatchVerifyUserEmailConfirmationRequest, match]);

  const normalRequiredField = Yup.string().max(256, 'Too long').required();

  const schema = Yup.object({
    password: normalRequiredField
      .required('Password is required.')
      .min(8, 'Password is too short.')
      .matches(
        /^(?=^.{8,}$)(?=.*\d)(?=.*[A-Z])(?=.*[a-z]).*$/,
        'Must Contain at least 8 Characters, One Uppercase, One Lowercase, One Number'
      ),
    confirmPassword: normalRequiredField
      .required('Confirmed Password is required.')
      .oneOf([Yup.ref('password'), null as any], 'Passwords must match.'),
  });

  const passwordSuccess = success ? (
    <div>
      <Info>{USER_EMAIL_CONFIRMATION_SUCCESS_MESSAGE}</Info>
      <Info>
        <Link className="link_underline" to={`/login`}>
          Login
        </Link>
      </Info>
    </div>
  ) : (
    <Formik
      initialValues={initialPasswordValues}
      validationSchema={schema}
      onSubmit={(values: SetPasswordValues) => {
        const userEmailConfirmationRequest: Bixby.Models.UserEmailConfirmationRequest = {
          userId: match?.params.userId ?? '',
          password: values.password,
          code: match?.params.code ?? '',
        };
        dispatchUserEmailConfirmationRequest(userEmailConfirmationRequest);
      }}
      render={({ handleSubmit }) => (
        <form className="login_form reset_password_form" onSubmit={handleSubmit}>
          <InputBlock title="Password">
            <Field type="password" name="password" />
          </InputBlock>
          <ErrorMessage name="password">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
          <InputBlock title="Confirm Password">
            <Field type="password" name="confirmPassword" />
          </InputBlock>
          <ErrorMessage name="confirmPassword">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
          <div className="button_center">
            <button className="bix_button" type="submit">
              Reset Password
            </button>
          </div>
        </form>
      )}></Formik>
  );

  const display = isLoading ? (
    <div>Loading...</div>
  ) : tokenInvalid ? (
    <Error>Invalid token</Error>
  ) : error ? (
    <Error>{error}</Error>
  ) : (
    passwordSuccess
  );

  return (
    <div className="loginPage">
      <div className="content_wrap forgot_password_box">
        <div className="logo_welcome">
          <Logo />
          Set your password
        </div>
        {display}
      </div>
      <div className="copy_foot">&copy; {new Date().getFullYear()} Bixby Research and Analytics LLC</div>
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.userEmailConfirmationState.isLoading,
    error: state.userEmailConfirmationState.error,
    success: state.userEmailConfirmationState.success,
    tokenInvalid: state.userEmailConfirmationState.tokenInvalid,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchUserEmailConfirmationRequest: (cmd: Bixby.Models.UserEmailConfirmationRequest) =>
    dispatch(userEmailConfirmationAction(cmd)),
  dispatchVerifyUserEmailConfirmationRequest: (cmd: Bixby.Models.VerifyPasswordResetTokenRequest) =>
    dispatch(verifyUserEmailConfirmationTokenAction(cmd)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserEmailConfirmation);
